import { useMemo } from 'react'
import { FinalizedReportPreview } from '@/hooks/api/finalizedReports'
import useI18n from '@/shared/hooks/useI18n'
import FinalizedReportQpdTable from './FinalizedReportQpdTable'

const QPDS_FOR_TABLE = ['QPD12', 'QPD13', 'QPD14', 'QPD15', 'QPD16']

function QpdLabel({ qpd, value, selectedPdfLanguage }: { qpd: string; value: number | string | undefined | null; selectedPdfLanguage?: string }) {
  const { t } = useI18n()

  return (
    <span>
      {t(`emissionQualifyingParams:fields.${qpd}.reportText`, { lng: selectedPdfLanguage, value })}
      <span className="text-xs text-text-secondary"> ({qpd})</span>
    </span>
  )
}

export default function FinalizedReportEmissionQualifyingParams({
  emissionQualifyingParams,
  selectedPdfLanguage
}: {
  emissionQualifyingParams: FinalizedReportPreview['emissionQualifyingParams']
  selectedPdfLanguage?: string
}) {
  const { t } = useI18n()
  const emissionQualifyingParamsArray = Object.keys(emissionQualifyingParams) as Array<
    keyof typeof emissionQualifyingParams
  >

  const qpdItems: Record<string, { label: string; value: number; key: string }[]> = useMemo(() => {
    return {
      QPD23:
        (emissionQualifyingParams.QPD23?.elements ?? []).map(element => ({
          label: element.name,
          value: element.value,
          key: element.name
        })) ?? [],
      QPD20:
        (emissionQualifyingParams.QPD20?.materials ?? []).map(material => ({
          label: material.name,
          value: material.value,
          key: material.name
        })) ?? []
    }
  }, [emissionQualifyingParams])

  const getTableLabel = (qpd: keyof typeof emissionQualifyingParams) => {
    const value = emissionQualifyingParams[qpd]?.value
    const label = (
      <span>
        {t(`emissionQualifyingParams:fields.${qpd}.reportText`, {
          lng: selectedPdfLanguage,
          value
        })}
        <span className="text-xs text-text-secondary"> ({qpd})</span>
      </span>
    )
    return label
  }

  const qpdRenderMap: Record<string, React.ReactNode> = {
    QPD01: (
      <li>
        - <QpdLabel qpd="QPD01" value={emissionQualifyingParams.QPD01?.value} selectedPdfLanguage={selectedPdfLanguage} />
      </li>
    ),
    QPD02: (
      <li>
        - <QpdLabel qpd="QPD02" value={emissionQualifyingParams.QPD02?.value} selectedPdfLanguage={selectedPdfLanguage} />
      </li>
    ),
    QPD10: (
      <li>
        - <QpdLabel qpd="QPD10" value={emissionQualifyingParams.QPD10?.value} selectedPdfLanguage={selectedPdfLanguage} />
      </li>
    ),
    QPD11: (
      <li>
        - <QpdLabel qpd="QPD11" value={emissionQualifyingParams.QPD11?.value} selectedPdfLanguage={selectedPdfLanguage} />
      </li>
    ),
    QPD17: (
      <li>
        - <QpdLabel qpd="QPD17" value={emissionQualifyingParams.QPD17?.value} selectedPdfLanguage={selectedPdfLanguage} />
      </li>
    ),
    QPD18: (
      <li>
        - <QpdLabel qpd="QPD18" value={emissionQualifyingParams.QPD18?.value} selectedPdfLanguage={selectedPdfLanguage} />
      </li>
    ),
    QPD19: (
      <li>
        - <QpdLabel qpd="QPD19" value={emissionQualifyingParams.QPD19?.value} selectedPdfLanguage={selectedPdfLanguage} />
      </li>
    ),
    QPD20:
      qpdItems.QPD20.length > 0 ? (
        <FinalizedReportQpdTable
          items={qpdItems.QPD20}
          label={getTableLabel('QPD20')}
          columnNames={['Material', 'Percentage']}
        />
      ) : null,
    QPD21: (
      <li>
        - <QpdLabel qpd="QPD21" value={emissionQualifyingParams.QPD21?.value} selectedPdfLanguage={selectedPdfLanguage} />
      </li>
    ),
    QPD22: (
      <li>
        - <QpdLabel qpd="QPD22" value={emissionQualifyingParams.QPD22?.value} selectedPdfLanguage={selectedPdfLanguage} />
      </li>
    ),
    QPD23:
      qpdItems.QPD23.length > 0 ? (
        <li>
          <FinalizedReportQpdTable
            items={qpdItems.QPD23}
            label={getTableLabel('QPD23')}
            columnNames={['Material', 'Percentage']}
          />
        </li>
      ) : null
  }

  const tableData = useMemo(() => {
    return QPDS_FOR_TABLE.map(qpd => {
      const value = emissionQualifyingParams[qpd as keyof typeof emissionQualifyingParams]?.value
      if (value === undefined || value === null) {
        return null
      }

      const label = <QpdLabel qpd={qpd} value={value} selectedPdfLanguage={selectedPdfLanguage} />

      return {
        key: qpd,
        label,
        value
      }
    }).filter((item): item is NonNullable<typeof item> => item !== null)
  }, [emissionQualifyingParamsArray, selectedPdfLanguage])

  function renderQpd(qpd: keyof typeof qpdRenderMap) {
    const isQpdRelevant =
      emissionQualifyingParams[qpd as keyof typeof emissionQualifyingParams]?.value !== undefined
    return isQpdRelevant ? qpdRenderMap[qpd] : null
  }

  return (
    <ul className="space-y-2">
      {renderQpd('QPD01')}
      {renderQpd('QPD02')}
      {renderQpd('QPD10')}
      {renderQpd('QPD11')}
      {tableData.length > 0 && (
        <FinalizedReportQpdTable
          items={tableData}
          label="Mass % of inputs for the iron, steel or alloy under consideration"
          columnNames={['Material', 'Mass %']}
        />
      )}
      {renderQpd('QPD17')}
      {renderQpd('QPD18')}
      {renderQpd('QPD19')}
      {renderQpd('QPD20')}
      {renderQpd('QPD21')}
      {renderQpd('QPD22')}
      {renderQpd('QPD23')}
    </ul>
  )
}
